/**
 * Router Provider
 *
 * TanStack Router provider with file-based routing.
 * Requires auth and ability context to be available.
 *
 * @layer app
 * @segment providers
 * @added DOC-0100 (2025-12-26)
 */

import { RouterProvider as TanStackRouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from '../../routeTree.gen';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { createRouterContext, type RouterContext } from '@shared/config/router-context';

// Create the router instance with context type
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // Context will be provided by the component
  context: undefined as unknown as RouterContext,
});

// Register the router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function RouterProviderWithContext() {
  const auth = useAuth();
  const ability = useAbility();

  return <TanStackRouterProvider router={router} context={createRouterContext(auth, ability)} />;
}

export { router };
